'use client';
import { Children, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { ModalBody, ModalFooter } from './Modal';
import { Button } from './Button';
import { Spinner } from './states';
import type { ConfirmDialogProps } from 'elowen-plugin-ui-kit';
import { useTranslation } from '../../lib/i18n';
import { useOperationFailure, useToast } from './Toast';
import { useOverlayIsolation } from './overlayStack';
import { OwnerHome } from './toastSurface';
import { holdsAnswer, releasedBy, releasing } from '../../lib/saveFeedback';
import { AlertDialog, AlertDialogContent } from './shadcn/alert-dialog';
import { DialogHeader, DialogOverlay } from './shadcn/dialog';

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof value === 'object' && value !== null && 'then' in value && typeof value.then === 'function';
}

type HostConfirmDialogProps = ConfirmDialogProps & {
  children?: ReactNode;
  /** The control that raised the question, when its own pending state disables it before this mounts. */
  returnFocusTo?: HTMLElement | null;
};

/** A confirmation, on the shadcn `AlertDialog` and therefore on Radix `@radix-ui/react-alert-dialog`.
 *
 *  The primitive is the point. An alert dialog is the one overlay that must NOT go away when the user
 *  presses outside it: it asks a single question about an action already chosen, usually a destructive
 *  one, and a stray click on the backdrop is not an answer. Radix enforces that inside
 *  `AlertDialogContent`, so it cannot be undone by a call site the way a hand-written backdrop handler
 *  could. Escape still closes an idle confirmation, because cancelling IS the safe answer. While an
 *  asynchronous confirmation is running, every dismissal path is blocked so the result always has an
 *  owner and a second confirmation cannot race the first one.
 *
 *  Mounted only while open, like every overlay here: `useOverlayIsolation` captures the element to
 *  return focus to on its first render, and a component that stayed mounted while closed would capture
 *  whatever happened to be focused when the page loaded. */
export function ConfirmDialog({ open, ...props }: HostConfirmDialogProps) {
  if (!open) return null;
  return <OpenConfirmDialog {...props} />;
}

function OpenConfirmDialog({
  title,
  description,
  children,
  confirmLabel,
  confirmVariant = 'danger',
  pendingLabel,
  pending = false,
  disabled = false,
  confirmDisabled = false,
  failureMessage,
  reportedBy,
  reload,
  onConfirm,
  onClose,
  returnFocusTo,
}: Omit<HostConfirmDialogProps, 'open'>) {
  const { t } = useTranslation();
  // The latest rejection of `onConfirm`. It is REPORTED by `ConfirmFailure`, rendered inside the dialog's own
  // surface: a hook called here sits above the surface and would raise the card under whatever is beneath it.
  // `holds`: the outcome is unknown or the answer no longer fits (see `reportFailure`).
  const [failure, setFailure] = useState<{ error: unknown; sequence: number; holds: boolean } | null>(null);
  const failures = useRef(0);
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmingRef = useRef(false);
  const activeRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [internalPending, setInternalPending] = useState(false);
  useEffect(() => {
    activeRef.current = true;
    setMounted(true);
    return () => { activeRef.current = false; };
  }, []);
  const { restoreFocus } = useOverlayIsolation({ enabled: mounted, rootRef: overlayRef, returnFocusTo });
  const isPending = pending || internalPending;
  const dismissBlocked = disabled || isPending;
  // A conflict or an unconfirmed outcome HOLDS the question: confirming the same answer again could repeat what
  // may already have happened (a rotated secret, a deleted record). Leaving stays possible; only a recovery that
  // actually settled releases it. A writer that reports elsewhere (`reportedBy`) keeps its own halt, passed as
  // `confirmDisabled`: no second policy here.
  const held = failure?.holds === true;
  const confirmBlocked = dismissBlocked || confirmDisabled || held;
  const close = () => { if (!dismissBlocked) onClose(); };
  // `children` may be `false` or `null` from `{cond && …}`, so count what React would actually render.
  const hasBody = Boolean(description) || Children.toArray(children).length > 0;

  // The awaited confirmation is the only reporter of its own failure: one keyed card, the dialog stays open and
  // unlocked, and nothing below it (a mutation hook, a caller) reports the same rejection again.
  const reportFailure = (error: unknown) => {
    const holds = reportedBy === undefined && holdsAnswer(error);
    setFailure({ error, sequence: ++failures.current, holds });
  };

  const confirm = () => {
    if (confirmBlocked || confirmingRef.current) return;
    confirmingRef.current = true;
    setFailure(null);

    let result: unknown;
    try {
      result = onConfirm();
    } catch (confirmError) {
      confirmingRef.current = false;
      reportFailure(confirmError);
      return;
    }

    if (!isPromiseLike(result)) {
      confirmingRef.current = false;
      return;
    }

    setInternalPending(true);
    void Promise.resolve(result).then(
      () => {
        confirmingRef.current = false;
        if (activeRef.current) setInternalPending(false);
      },
      (confirmError) => {
        confirmingRef.current = false;
        if (activeRef.current) {
          setInternalPending(false);
          reportFailure(confirmError);
        }
      },
    );
  };

  if (!mounted) return null;
  return createPortal(
    // A confirmation is a centered dialog wherever it is raised. It asks one question about the
    // action you just chose; sliding it in as a drawer would read as another settings surface.
    <AlertDialog open onOpenChange={(next) => { if (!next) close(); }}>
      <DialogOverlay ref={overlayRef} presentation="center">
        <AlertDialogContent
          ref={dialogRef}
          presentation="center"
          size="sm"
          // Without a body the header's hairline already divides header from footer; drop the footer's
          // own so the two do not stack into a doubled line.
          className="[&>[data-slot=dialog-header]+[data-slot=dialog-footer]]:border-t-0"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          aria-busy={isPending || undefined}
          data-elowen-modal
          onEscapeKeyDown={(event) => { if (dismissBlocked) event.preventDefault(); }}
          // A destructive question starts on its SAFE answer. The wrapper has no Radix Trigger, so keep
          // ownership of focus restoration below while explicitly choosing Cancel on entry.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            cancelRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            restoreFocus();
          }}
        >
          {reportedBy !== undefined ? <OwnerHome owner={reportedBy} /> : <ConfirmFailure failure={failure} failureMessage={failureMessage} reload={reload} onClose={onClose} onSettled={() => setFailure(null)} />}
          <DialogHeader title={title} titleId={titleId} icon={AlertTriangle} closeLabel={t.common.close} closeDisabled={dismissBlocked} onClose={close} />
          {hasBody ? (
          <ModalBody>
            {/* `whitespace-pre-line`: a confirmation that lists what is about to happen needs its lines to
                survive. Single-paragraph descriptions read identically.
                THE QUESTION COMES FIRST. `children` is the slot for controls that refine the answer —
                "also delete the files", the person being removed — so it renders AFTER the description:
                nobody should meet a checkbox about consequences before the consequence is stated. */}
            {description ? (
              <p id={descriptionId} className="whitespace-pre-line text-meta text-muted-foreground [overflow-wrap:anywhere]">{description}</p>
            ) : null}
            {children}
          </ModalBody>
          ) : null}
          <ModalFooter>
            <Button ref={cancelRef} variant="ghost" onClick={close} disabled={dismissBlocked}>{t.common.cancel}</Button>
            <Button variant={confirmVariant} onClick={confirm} disabled={confirmBlocked}>
              {isPending ? <Spinner tone="text-current" /> : null}
              {isPending ? (pendingLabel ?? confirmLabel ?? t.common.delete) : (confirmLabel ?? t.common.delete)}
            </Button>
          </ModalFooter>
        </AlertDialogContent>
      </DialogOverlay>
    </AlertDialog>,
    document.body,
  );
}

/** Reports the dialog's failure from inside its surface (its toast scope), so a conflict keeps its recoveries
 *  and a lost connection its safe exit, and the card ends with the dialog. It renders nothing. */
function ConfirmFailure({ failure, failureMessage, reload, onClose, onSettled }: {
  failure: { error: unknown; sequence: number } | null;
  failureMessage: HostConfirmDialogProps['failureMessage'];
  reload: HostConfirmDialogProps['reload'];
  onClose: () => void;
  /** A recovery of this failure ran to completion: the hold, if any, ends. */
  onSettled: () => void;
}) {
  const { t } = useTranslation();
  const { dismissKey } = useToast();
  const report = useOperationFailure();
  const key = `confirm:${useId()}`;
  const latest = useRef({ failureMessage, reload, onClose, onSettled, fallback: t.common.actionFailed });
  latest.current = { failureMessage, reload, onClose, onSettled, fallback: t.common.actionFailed };
  useEffect(() => {
    if (!failure) { dismissKey(key); return; }
    const { failureMessage: message, reload: reread, fallback } = latest.current;
    // A lost connection leaves the confirmation unconfirmed. The domain's `reload` re-reads what it changed and is
    // the only recovery labelled as one; without it the card offers closing the dialog, labelled as that, because
    // closing verifies nothing. Nothing resends the operation. Both are read when pressed.
    // A recovery releases the hold only once it has completed: one that rejects leaves the question held.
    const released = () => latest.current.onSettled();
    const recovery = reread ? { reload: releasing(() => latest.current.reload?.(), released) } : { leave: () => latest.current.onClose() };
    report(key, releasedBy(failure.error, released), { message: message?.(failure.error) ?? fallback, ...recovery });
  }, [failure, dismissKey, report, key]);
  return null;
}
